// WhatsApp UI Styles - Matching HTML Mockup Design Exactly

// Hoisted at module top so emotion emits the rule once rather than re-
// serialising it on every getStyles() call (getStyles runs on every
// ChatPage render). Reference this string from any style object via
// `animation: `${WA_MSG_ARRIVE_KEYFRAMES} 200ms ease-out both``.
const WA_MSG_ARRIVE_KEYFRAMES_NAME = 'waMsgArrive';
// Optimistic-send "pop" (scale 0.98→1 + fade) and a plain fade used as the
// reduced-motion fallback for all bubble entrances. Shared motion tokens:
// every WhatsApp bubble/control transition is ease-out, 150–200ms.
const WA_MSG_POP_KEYFRAMES_NAME = 'waMsgPop';
const WA_MSG_FADE_KEYFRAMES_NAME = 'waMsgFade';

// Colors from design system
const COLORS = {
  primary: '#00A76F',
  primaryDark: '#007B55',
  primaryLight: 'rgba(0, 167, 111, 0.16)',
  primaryLighter: 'rgba(0, 167, 111, 0.08)',
  background: '#F4F6F8',
  backgroundLight: '#F9FAFB',
  paper: '#FFFFFF',
  textPrimary: '#212B36',
  textSecondary: '#637381',
  textDisabled: '#919EAB',
  textBody: '#454F5B',
  divider: 'rgba(145, 158, 171, 0.2)',
  dividerLight: 'rgba(145, 158, 171, 0.12)',
  dividerMedium: 'rgba(145, 158, 171, 0.16)',
  dividerLighter: 'rgba(145, 158, 171, 0.08)',
  warning: '#FFAB00',
  warningDark: '#B76E00',
  warningLight: '#7A4F01',
  warningBg: 'rgba(255, 171, 0, 0.08)',
  warningBorder: 'rgba(255, 171, 0, 0.24)',
  error: '#FF5630',
  errorDark: '#B71D18',
  errorBg: 'rgba(255, 86, 48, 0.12)',
  info: '#00B8D9',
  infoDark: '#006C9C',
  infoLight: 'rgba(0, 184, 217, 0.16)',
  purple: '#8E33FF',
  cyan: '#00B8D9',
  amber: '#FFAB00',
  sent: '#DCF1DE',
  sentText: '#1A2027',
  received: '#FFFFFF',
  chatBg: '#F0F2F5',
  teal: '#008069',
  whatsappTimestamp: '#667781',
  avatarGreen: '#22C55E',
  avatarIndigo: '#6366F1',
  avatarAmber: '#F59E0B',
  avatarRed: '#EF4444',
};

export { COLORS };

// Anything ≤ this width uses the mobile single-pane layout (shows Contacts
// OR ChatPage, never both). Raised from 768 → 1024 because at widths
// between 768–1024 the 380 px Contacts sidebar + desktop ChatPage empty-
// state panel can't both fit without overlapping/squashing — tablets and
// small laptops should stay single-pane for the same reason phones do.
export const WHATSAPP_MOBILE_BREAKPOINT = 1024;
// Compact composer breakpoint — above mobile, below full desktop.
// Rationale: sidebar NAV.W_DASHBOARD (228) + Contacts panel (380) = 608 px
// already consumed before ChatPage gets any width. At ≤ 1100 px viewport
// the composer has < ~480 px to fit template button, emoji, attach, AI,
// 4-button formatting toolbar, textarea AND a 34 px send button — the
// textarea ends up as the squeezed element. Between 768 and 1100 we
// collapse the formatting toolbar behind an "Aa" toggle and drop the
// Template button label so typing space stays generous.
export const WHATSAPP_COMPACT_BREAKPOINT = 1100;

// Icon colors for action buttons
export const ICON_COLORS = {
  details: '#00B8D9',
  tag: '#FFAB00',
  assign: '#8E33FF',
  treatment: '#00A76F',
  unread: '#637381',
};

export const getStyles = (theme, { isMobile = false } = {}) => ({
  // ============================================
  // CHAT CONTAINER STYLES
  // ============================================
  containerStyle: {
    flex: 1,
    display: 'flex',
    // IMPORTANT: must stay `column`. ChatPage.jsx also uses this as its
    // own root style (header → messages → footer input stacked). The
    // parent wrapper that puts Contacts + ChatPage side-by-side on
    // desktop applies its own `flexDirection: row` override in
    // Whatsapp.jsx's mainContainerStyle — don't change this one.
    flexDirection: 'column',
    height: '100%',
    backgroundColor: COLORS.paper,
    overflow: 'hidden',
    // Scroll-to-bottom FAB anchors to this relative root so it floats
    // above the message pane and just above the composer footer.
    position: 'relative',
  },

  // ============================================
  // CHAT HEADER STYLES
  // ============================================
  header: {
    padding: '10px 16px',
    borderBottom: '1px solid rgba(145, 158, 171, 0.2)',
    backgroundColor: COLORS.paper,
  },
  chatContactRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  avatarBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  chatName: {
    fontSize: '14px',
    fontWeight: 600,
    color: COLORS.textPrimary,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
    flexWrap: 'wrap',
  },
  statusBadge: {
    backgroundColor: COLORS.primaryLight,
    color: COLORS.primaryDark,
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
  },
  chatSubtitle: {
    fontSize: '12px',
    color: COLORS.textSecondary,
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    flexWrap: 'wrap',
    minWidth: 0,
  },
  chatTreatmentText: {
    minWidth: 0,
    maxWidth: isMobile ? '150px' : '220px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  windowPill: (isOpen) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: isOpen ? COLORS.primaryLight : COLORS.warningBg,
    color: isOpen ? COLORS.primaryDark : COLORS.warningDark,
  }),
  windowPillDot: (isOpen) => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: isOpen ? COLORS.primary : COLORS.warning,
    display: 'inline-block',
  }),
  workflowPill: (tone) => ({
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: {
      success: 'rgba(0, 167, 111, 0.08)',
      closed: COLORS.dividerLighter,
      warning: COLORS.warningBg,
    }[tone] || COLORS.warningBg,
    color: {
      success: COLORS.primaryDark,
      closed: COLORS.textSecondary,
      warning: COLORS.warningDark,
    }[tone] || COLORS.warningDark,
  }),
  headerSignalPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '4px',
    backgroundColor: COLORS.dividerLighter,
    color: COLORS.textSecondary,
  },
  closeBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#919EAB',
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.08)',
      color: '#637381',
    },
    ...(isMobile && { minWidth: '44px', minHeight: '44px' }),
  },
  resolveBtn: {
    minWidth: 0,
    height: '32px',
    px: 1.25,
    borderRadius: '8px',
    border: '1px solid rgba(0, 167, 111, 0.24)',
    backgroundColor: 'rgba(0, 167, 111, 0.08)',
    color: COLORS.primary,
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': {
      mr: 0.5,
      ml: 0,
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 167, 111, 0.14)',
      borderColor: 'rgba(0, 167, 111, 0.36)',
      color: COLORS.primary,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      opacity: 0.4,
      color: '#C4CDD5',
      borderColor: 'rgba(145, 158, 171, 0.16)',
    },
    ...(isMobile && { minHeight: '44px' }),
  },
  reopenBtn: {
    minWidth: 0,
    height: '32px',
    px: 1.25,
    borderRadius: '8px',
    backgroundColor: COLORS.errorBg,
    color: COLORS.errorDark,
    border: `1px solid rgba(255, 86, 48, 0.22)`,
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': {
      mr: 0.5,
      ml: 0,
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 86, 48, 0.14)',
      boxShadow: 'none',
    },
    ...(isMobile && { minHeight: '44px' }),
  },
  secondaryToolbarButton: {
    minWidth: 0,
    height: '32px',
    px: 1.1,
    borderRadius: '8px',
    border: '1px solid rgba(145, 158, 171, 0.2)',
    backgroundColor: '#fff',
    color: '#212B36',
    fontSize: '13px',
    fontWeight: 600,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '& .MuiButton-startIcon': {
      mr: 0.5,
      ml: 0,
    },
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.08)',
      borderColor: 'rgba(145, 158, 171, 0.32)',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      opacity: 0.45,
    },
    ...(isMobile && { minHeight: '44px' }),
  },
  createLeadBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#637381',
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.08)',
      color: '#212B36',
    },
    '&.Mui-disabled': {
      opacity: 0.4,
    },
    ...(isMobile && { minWidth: '44px', minHeight: '44px' }),
  },
  toolbarMenuPaper: {
    minWidth: 190,
    borderRadius: '10px',
    boxShadow: '0 10px 30px rgba(33, 43, 54, 0.14)',
    border: '1px solid rgba(145, 158, 171, 0.16)',
    mt: 0.75,
  },
  toolbarMenuItem: {
    gap: 1.25,
    minHeight: 36,
    px: 1.5,
    fontSize: '13px',
    fontWeight: 500,
    color: '#212B36',
    '& svg': {
      color: '#637381',
      flexShrink: 0,
    },
  },
  tagStyles: {
    borderRadius: '6px',
    padding: '2px 6px',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': {
      opacity: 0.85,
    },
  },

  // ============================================
  // ACTION BAR STYLES
  // ============================================
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 12px',
    backgroundColor: COLORS.background,
    borderRadius: '10px',
    overflowX: 'auto',
    overflowY: 'hidden',
    flexWrap: 'nowrap',
    ...(isMobile && { WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain' }),
    // Thin scrollbar styles
    '&::-webkit-scrollbar': {
      height: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(145, 158, 171, 0.3)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.5)',
    },
    // Firefox scrollbar
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(145, 158, 171, 0.3) transparent',
  },
  actionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderRadius: '6px',
    border: '1px solid rgba(145, 158, 171, 0.16)',
    backgroundColor: COLORS.paper,
    color: COLORS.textPrimary,
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    transition: 'all 0.15s',
    height: '32px',
    textTransform: 'none',
    minWidth: 'auto',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.16)',
    },
  },
  actionBtnActive: {
    backgroundColor: COLORS.infoLight,
    color: COLORS.infoDark,
  },
  closeConversationBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 14px',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: COLORS.warning,
    backgroundColor: COLORS.warningBg,
    color: COLORS.warningDark,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    height: '34px',
    marginLeft: 'auto',
    textTransform: 'none',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: 'rgba(255, 171, 0, 0.16)',
      borderColor: COLORS.warningDark,
    },
  },
  // Compact action icon buttons (icon-only with Tooltip)
  actionIconBtn: {
    width: 34,
    height: 34,
    borderRadius: '8px',
    backgroundColor: COLORS.paper,
    color: COLORS.textSecondary,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    '&:hover': {
      backgroundColor: COLORS.dividerLighter,
      color: COLORS.textPrimary,
    },
    ...(isMobile && { width: 44, height: 44, minWidth: 44, minHeight: 44 }),
  },
  actionIconBtnActive: {
    backgroundColor: COLORS.infoLight,
    color: COLORS.infoDark,
  },
  overflowBtn: {
    width: 34,
    height: 34,
    borderRadius: '8px',
    color: COLORS.textDisabled,
    '&:hover': {
      backgroundColor: COLORS.dividerLighter,
    },
  },
  bookAppointmentBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 14px',
    borderRadius: '8px',
    backgroundColor: COLORS.primary,
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    height: '34px',
    textTransform: 'none',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 6px rgba(0, 167, 111, 0.3)',
    '&:hover': {
      backgroundColor: COLORS.primaryDark,
      boxShadow: '0 4px 10px rgba(0, 167, 111, 0.4)',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(0, 167, 111, 0.24)',
      color: 'rgba(255, 255, 255, 0.6)',
      boxShadow: 'none',
    },
  },
  // Quick close popover
  quickCloseMenu: {
    minWidth: 220,
    py: 0.5,
  },
  quickCloseTitle: {
    px: 2,
    py: 1,
    fontSize: '11px',
    fontWeight: 700,
    color: COLORS.textDisabled,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  quickCloseItem: {
    px: 2,
    py: 1,
    fontSize: '13px',
    color: COLORS.textPrimary,
    '&:hover': {
      backgroundColor: COLORS.dividerLighter,
    },
  },
  quickCloseMore: {
    px: 2,
    py: 1,
    fontSize: '12px',
    color: COLORS.textSecondary,
    gap: 1,
  },
  // Close conversation popup styles
  closeContactSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    mb: 2,
    p: 1.5,
    backgroundColor: COLORS.dividerLighter,
    borderRadius: '10px',
  },
  closeContactInfo: {
    fontSize: '13px',
    color: COLORS.textSecondary,
  },
  closeDetailToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    py: 0.5,
    mb: 1,
    '&:hover': {
      color: COLORS.primary,
    },
  },
  closeDetailContent: {
    pl: 2,
    pb: 1.5,
    mb: 1,
    borderLeft: `2px solid ${COLORS.dividerLight}`,
  },
  closeFootnote: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    color: COLORS.textDisabled,
    mt: 1.5,
  },

  // ============================================
  // MESSAGE AREA STYLES
  // ============================================
  messagePane: {
    flexGrow: 1,
    padding: isMobile ? '12px 10px' : '20px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: COLORS.chatBg,
    // Subtle original doodle pattern — dots + arcs at ~3% opacity
    // tile at 240px. Not copyrighted art; built from primitives so it
    // reads as atmospheric texture, not brand replication.
    backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 240 240'>
<g fill='none' stroke='%23212B36' stroke-width='1.1' stroke-linecap='round' opacity='0.035'>
<circle cx='30' cy='40' r='2.2' fill='%23212B36' stroke='none'/>
<circle cx='190' cy='70' r='2.2' fill='%23212B36' stroke='none'/>
<circle cx='120' cy='200' r='2.2' fill='%23212B36' stroke='none'/>
<circle cx='70' cy='160' r='1.6' fill='%23212B36' stroke='none'/>
<circle cx='210' cy='180' r='1.6' fill='%23212B36' stroke='none'/>
<circle cx='160' cy='30' r='1.6' fill='%23212B36' stroke='none'/>
<path d='M50 110 Q 80 90, 110 110'/>
<path d='M140 150 Q 170 135, 200 150'/>
<path d='M20 220 Q 45 205, 70 220'/>
<path d='M170 215 Q 195 200, 220 215'/>
<path d='M90 60 Q 105 48, 120 60'/>
</g>
</svg>`
    )}")`,
    backgroundRepeat: 'repeat',
    backgroundSize: '240px 240px',
  },
  messagesStart: {
    textAlign: 'center',
    color: COLORS.textDisabled,
    fontSize: '12px',
    marginBottom: '20px',
  },
  emptyConversationState: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: '8px',
    px: 2,
  },
  emptyConversationIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 167, 111, 0.1)',
    color: COLORS.primary,
  },
  emptyConversationTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  emptyConversationBody: {
    fontSize: '13px',
    color: COLORS.textSecondary,
    maxWidth: 320,
    lineHeight: 1.45,
  },
  emptyConversationButton: {
    mt: 0.5,
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 700,
    borderRadius: '8px',
    color: COLORS.primary,
    backgroundColor: COLORS.primaryLight,
    '&:hover': {
      backgroundColor: 'rgba(0, 167, 111, 0.14)',
    },
  },
  messageActionHint: {
    position: 'absolute',
    left: '50%',
    bottom: isMobile ? 12 : 16,
    transform: 'translateX(-50%)',
    zIndex: 9,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    maxWidth: 'calc(100% - 28px)',
    px: 1,
    py: 0.75,
    borderRadius: '999px',
    backgroundColor: 'rgba(255, 255, 255, 0.96)',
    border: `1px solid ${COLORS.divider}`,
    boxShadow: '0 8px 22px rgba(17, 24, 39, 0.12), 0 1px 3px rgba(17, 24, 39, 0.06)',
    color: COLORS.textSecondary,
  },
  messageActionHintText: {
    fontSize: '12px',
    fontWeight: 600,
    color: COLORS.textSecondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  messageActionHintClose: {
    width: 22,
    height: 22,
    color: COLORS.textDisabled,
    '&:hover': {
      backgroundColor: COLORS.dividerLighter,
      color: COLORS.textPrimary,
    },
  },
  dateDivider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '16px 0',
    // Tier 3 / F-34 — sticky date header.
    // The pane uses `flexDirection: column-reverse` and the date
    // divider is rendered AFTER its message (visually above). With
    // `position: sticky; top: 0`, the topmost on-screen date label
    // pins to the top of the scroll viewport while older messages
    // scroll under it. The opaque pill in `dateDividerText` keeps
    // bubbles from showing through. Graceful degradation: if a
    // browser ignores `sticky`, the pill renders inline as before.
    position: 'sticky',
    top: 0,
    zIndex: 5,
    // Slim background strip behind the pill so even if a wide
    // bubble scrolls under, no text peeks past the pill edges.
    backgroundColor: 'transparent',
    pointerEvents: 'none',
  },
  dateDividerText: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: '#54656F',
    fontSize: '10.5px',
    fontWeight: 500,
    padding: '5px 12px',
    borderRadius: '999px',
    letterSpacing: '0.2px',
    // Tier 3 / F-34 — slightly stronger shadow on the bottom edge so
    // the pinned label visibly separates from the messages scrolling
    // under it. Keep horizontal/top shadow contribution minimal.
    boxShadow: '0 2px 4px rgba(11, 20, 26, 0.18)',
    textTransform: 'uppercase',
  },
  // Tier 3 / F-33 — floating "N unread ↓" jump button. Anchors to the
  // chat container's relative root, sits below the header at the top
  // of the message pane.
  unreadJumpBtn: {
    position: 'absolute',
    // Top offset accounts for the chat header (~64 px) + pinned
    // banner cushion. The composer FAB lives at the bottom so we
    // own the top-right corner here.
    top: '88px',
    right: isMobile ? '12px' : '20px',
    // Theme error red per spec.
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '999px',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.3px',
    boxShadow: '0 6px 16px rgba(211, 47, 47, 0.32), 0 1px 3px rgba(0, 0, 0, 0.08)',
    zIndex: 10,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, opacity 0.2s ease',
    '&:hover': {
      backgroundColor: '#b71c1c',
      transform: 'translateY(-1px)',
      boxShadow: '0 8px 20px rgba(211, 47, 47, 0.4), 0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },

  // Message-arrival animation — applied only to newly-received messages
  // once the initial chat load has settled. Slide up ~10px + fade in over
  // 200ms. No bounce, no overshoot. Emotion deduplicates the @keyframes
  // block by content hash so re-emitting it each render has no runtime
  // cost; keeping it co-located for readability.
  messageArriveReceived: {
    animation: `${WA_MSG_ARRIVE_KEYFRAMES_NAME} 200ms ease-out both`,
    [`@keyframes ${WA_MSG_ARRIVE_KEYFRAMES_NAME}`]: {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    // Reduced motion: drop the slide, keep a plain fade.
    '@media (prefers-reduced-motion: reduce)': {
      animation: `${WA_MSG_FADE_KEYFRAMES_NAME} 200ms ease-out both`,
      [`@keyframes ${WA_MSG_FADE_KEYFRAMES_NAME}`]: {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
    },
  },
  // Optimistic-send pop — a subtle scale-up + fade so sending feels instant.
  messageArriveSent: {
    animation: `${WA_MSG_POP_KEYFRAMES_NAME} 150ms ease-out both`,
    [`@keyframes ${WA_MSG_POP_KEYFRAMES_NAME}`]: {
      from: { opacity: 0, transform: 'scale(0.98)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    // Reduced motion: drop the scale, keep a plain fade.
    '@media (prefers-reduced-motion: reduce)': {
      animation: `${WA_MSG_FADE_KEYFRAMES_NAME} 150ms ease-out both`,
      [`@keyframes ${WA_MSG_FADE_KEYFRAMES_NAME}`]: {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
    },
  },

  // ============================================
  // MESSAGE BUBBLE STYLES - MATCHING HTML EXACTLY
  // ============================================
  messageWrapper: {
    display: 'flex',
    // Tighter gap inside a same-sender group; larger margin between groups
    // is applied via `messageWrapperGroupEnd` when `isLastInGroup` is true.
    marginBottom: '2px',
    alignItems: 'flex-end',
  },
  messageWrapperGroupEnd: {
    // Extra space below the final message of a run — gives conversation
    // visible rhythm without the old uniform 24px gap.
    marginBottom: '16px',
  },
  messageWrapperSent: {
    justifyContent: 'flex-end',
  },
  messageWrapperReceived: {
    justifyContent: 'flex-start',
  },
  messageContent: {
    maxWidth: isMobile ? '82%' : '65%',
  },
  messageBubble: {
    padding: '6px 12px',
    fontSize: '14px',
    lineHeight: 1.5,
    position: 'relative',
  },
  // Default radius — used when bubble is NOT the first in a run
  // (no tail drawn; rounded on the "away" side, tighter on the tail side).
  messageBubbleSent: {
    backgroundColor: COLORS.sent,
    color: COLORS.sentText,
    borderRadius: '12px 4px 12px 12px',
    // Outer drop shadow + 0.5 px inset highlight on the top edge.
    // The inset gives the iconic WA "lifted" feel on the green bubble
    // — barely visible individually, big difference cumulatively.
    boxShadow: '0 1px 0.5px rgba(11,20,26,0.13), inset 0 0.5px 0 rgba(255,255,255,0.6)',
  },
  // A reply the AI wrote and sent on its own. It is still the practice's
  // outgoing message, so it keeps the sent bubble's shape and side — moving it
  // or recolouring it wholesale would read as a different kind of message
  // altogether. What it gains is an indigo edge and a tinted ground, so
  // scanning a thread answers "did a person write this?" without reading a
  // word of it. The old treatment was a 9px pill tucked beside the timestamp,
  // next to the ticks, at the size of the "(edited)" marker: findable once you
  // knew it was there, invisible otherwise.
  messageBubbleAi: {
    backgroundColor: '#E8EBFD',
    color: COLORS.textPrimary,
    borderRadius: '12px 4px 12px 12px',
    borderRight: '3px solid #6366F1',
    boxShadow: '0 1px 0.5px rgba(11,20,26,0.13), inset 0 0.5px 0 rgba(255,255,255,0.6)',
  },
  // The test harness talking, not the agent. A live-test conversation resets
  // itself after a handoff and says so in the thread, so those lines sit among
  // real replies and read exactly like them — the one bubble a tester must not
  // mistake for something a patient would ever receive. Amber, dashed, and the
  // AI bubble's shape so it is still obviously outbound.
  messageBubbleTestMode: {
    backgroundColor: '#FFF4E0',
    color: COLORS.textPrimary,
    borderRadius: '12px 4px 12px 12px',
    borderRight: '3px dashed #B76E00',
    boxShadow: '0 1px 0.5px rgba(11,20,26,0.13), inset 0 0.5px 0 rgba(255,255,255,0.6)',
  },
  messageTestModeLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '2px',
    color: '#B76E00',
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
  },
  messageBubbleFirstAi: {
    borderTopRightRadius: 0,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: -7,
      width: 8,
      height: 12,
      background: '#6366F1',
      clipPath: 'polygon(0 0, 100% 0, 0 100%)',
      filter: 'drop-shadow(0 1px 0.5px rgba(11,20,26,0.08))',
      pointerEvents: 'none',
    },
  },
  messageAiLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: '2px',
    color: '#4338CA',
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
  },
  messageBubbleTemplate: {
    backgroundColor: COLORS.sent,
    color: COLORS.sentText,
    borderRadius: '12px 4px 12px 12px',
    border: '1px dashed rgba(0, 167, 111, 0.3)',
    boxShadow: '0 1px 0.5px rgba(11,20,26,0.13), inset 0 0.5px 0 rgba(255,255,255,0.6)',
  },
  messageBubbleReceived: {
    backgroundColor: COLORS.received,
    color: COLORS.textPrimary,
    borderRadius: '4px 12px 12px 12px',
    // Same recipe on the white bubble — a slightly darker inset to read
    // against white instead of the green-on-green pairing above.
    boxShadow: '0 1px 0.5px rgba(11,20,26,0.13), inset 0 0.5px 0 rgba(11,20,26,0.04)',
  },
  // First bubble in a same-sender run — top corner on the tail side is
  // sharp/flat and a small triangular tail is drawn via ::before. The
  // clip-path builds a pointed silhouette that reads as WA's native tail
  // without inline SVG or mask images.
  messageBubbleFirstSent: {
    borderTopRightRadius: 0,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: -7,
      width: 8,
      height: 12,
      background: COLORS.sent,
      clipPath: 'polygon(0 0, 100% 0, 0 100%)',
      filter: 'drop-shadow(0 1px 0.5px rgba(11,20,26,0.08))',
      // Decorative only — must not intercept clicks on the bubble
      // action menu or reactions UI that sits nearby.
      pointerEvents: 'none',
    },
  },
  messageBubbleFirstReceived: {
    borderTopLeftRadius: 0,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: -7,
      width: 8,
      height: 12,
      background: COLORS.received,
      clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
      filter: 'drop-shadow(0 1px 0.5px rgba(11,20,26,0.08))',
      pointerEvents: 'none',
    },
  },
  messageMetaInline: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    float: 'right',
    marginLeft: '8px',
    marginTop: '4px',
    position: 'relative',
    top: '4px',
  },
  messageTimeInline: {
    fontSize: '11px',
    color: COLORS.whatsappTimestamp,
  },
  messageStatus: {
    color: COLORS.primary,
  },

  // Old styles for compatibility
  messageRow: {
    maxWidth: '65%',
    padding: '10px 14px',
    fontSize: '14px',
    lineHeight: 1.5,
    width: 'fit-content',
    marginBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  sent: {
    alignSelf: 'flex-end',
    backgroundColor: COLORS.sent,
    color: COLORS.sentText,
    borderRadius: '8px',
  },
  received: {
    alignSelf: 'flex-start',
    backgroundColor: COLORS.received,
    color: COLORS.textPrimary,
    borderRadius: '8px',
  },
  timestamp: {
    fontSize: '11px',
    color: COLORS.whatsappTimestamp,
  },
  timestampSent: {
    fontSize: '11px',
    color: COLORS.whatsappTimestamp,
  },

  // Message avatar
  messageAvatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: '11px',
    marginRight: '8px',
    flexShrink: 0,
  },
  messageGroup: {
    marginBottom: '24px',
  },
  messageSender: {
    fontSize: '12px',
    fontWeight: 600,
    color: COLORS.textSecondary,
    marginBottom: '4px',
    marginLeft: '36px',
  },
  reaction: {
    background: COLORS.textPrimary,
    padding: '1px 4px',
    borderRadius: '16px',
    position: 'absolute',
    left: 0,
    bottom: '-8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
  },
  avatarStyles: {
    width: '28px',
    height: '28px',
    border: '2px solid #ccc',
    marginBottom: '4px',
    fontSize: '10px',
    color: 'white',
  },

  // ============================================
  // SYSTEM NOTIFICATION STYLES
  // ============================================
  system: {
    margin: '8px auto',
    padding: '8px 14px',
    borderRadius: '8px',
    fontSize: '12.5px',
    lineHeight: 1.5,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: '#54656F',
    textAlign: 'center',
    maxWidth: '85%',
    boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)',
  },
  systemWarning: {
    backgroundColor: COLORS.warningBg,
    borderLeftColor: COLORS.warning,
    color: COLORS.warningLight,
  },
  systemError: {
    backgroundColor: 'rgba(255, 86, 48, 0.08)',
    borderLeftColor: COLORS.error,
    color: COLORS.errorDark,
  },
  systemSuccess: {
    backgroundColor: COLORS.primaryLighter,
    borderLeftColor: COLORS.primary,
    color: COLORS.primaryDark,
  },

  // ============================================
  // ADD MEDIA BUTTON STYLES
  // ============================================
  addMediaBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '6px',
    backgroundColor: 'rgba(0, 167, 111, 0.08)',
    color: COLORS.primaryDark,
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 167, 111, 0.16)',
    },
  },
  addMediaBtnSuccess: {
    backgroundColor: 'rgba(0, 167, 111, 0.12)',
    color: COLORS.primary,
  },

  // ============================================
  // INTERACTIVE MESSAGE STYLES
  // ============================================
  interactiveMessage: {
    background: '#FFF5CC',
    color: COLORS.warningLight,
    minWidth: '160px',
  },
  // Template indicator — icon-only with tooltip
  templateIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    marginTop: '4px',
    padding: '2px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 167, 111, 0.10)',
    cursor: 'help',
  },
  // Collapsed template message — compact pill
  templateCollapsed: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '12px',
    backgroundColor: 'rgba(0, 167, 111, 0.06)',
    border: '1px dashed rgba(0, 167, 111, 0.25)',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: 'rgba(0, 167, 111, 0.12)',
    },
  },

  // ============================================
  // DATA PILLS STYLES
  // ============================================
  dataPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    margin: '8px 0',
    justifyContent: 'flex-end',
  },
  dataPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: '#FFFFFF',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    borderLeft: '3px solid transparent',
  },
  dataPillEmail: {
    borderLeftColor: '#FF5630',
    color: '#B71D18',
  },
  dataPillTreatment: {
    borderLeftColor: '#FFAB00',
    color: '#B76E00',
  },
  dataPillPhone: {
    borderLeftColor: '#00B8D9',
    color: '#006C9C',
  },
  dataPillAppointment: {
    borderLeftColor: '#00A76F',
    color: '#007B55',
  },
  dataPillComment: {
    borderLeftColor: '#919EAB',
    color: '#637381',
  },

  // ============================================
  // 24-HOUR WINDOW ALERT STYLES - MATCHING HTML EXACTLY
  // ============================================
  windowAlert: {
    margin: '12px auto',
    padding: '16px',
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)',
    maxWidth: isMobile ? '95%' : '85%',
  },
  windowAlertHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  windowAlertIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: 'rgba(145, 158, 171, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#54656F',
    flexShrink: 0,
  },
  windowAlertTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#212B36',
  },
  windowAlertSubtitle: {
    fontSize: '12px',
    color: '#54656F',
  },
  windowAlertProgress: {
    marginTop: '12px',
  },
  windowAlertProgressBar: {
    height: '4px',
    backgroundColor: 'rgba(145, 158, 171, 0.2)',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  windowAlertProgressFill: {
    height: '100%',
    backgroundColor: '#919EAB',
    borderRadius: '2px',
    transition: 'width 0.3s',
  },
  windowAlertTime: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '6px',
    fontSize: '11px',
    color: '#54656F',
  },

  // ============================================
  // COLLAPSED WINDOW ALERT STYLES
  // ============================================
  windowAlertCollapsed: {
    margin: '8px auto',
    padding: '8px 12px',
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '8px',
    border: 'none',
    boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    maxWidth: '85%',
    '&:hover': {
      background: 'rgba(255, 255, 255, 1)',
    },
  },
  windowAlertCollapsedText: {
    fontSize: '12px',
    color: '#54656F',
    flex: 1,
  },

  // ============================================
  // TEMPLATE REQUIRED NOTICE STYLES (kept for backward compat)
  // ============================================
  templateRequiredNotice: {
    margin: '16px 0',
    padding: '14px 16px',
    backgroundColor: '#FFF4E5',
    borderRadius: '12px',
    border: `1px dashed ${COLORS.warning}`,
    textAlign: 'center',
  },
  templateRequiredText: {
    fontSize: '13px',
    color: COLORS.warningLight,
    margin: 0,
  },

  // ============================================
  // MESSAGE INPUT AREA STYLES
  // ============================================
  footer: {
    padding: '12px 16px',
    borderTop: `1px solid ${COLORS.divider}`,
    backgroundColor: COLORS.paper,
    position: 'relative',
  },
  windowStatus: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: isMobile ? '4px 12px' : '8px',
    marginBottom: '8px',
    fontSize: '11px',
    fontWeight: 500,
  },
  windowStatusActive: {
    color: COLORS.primaryDark,
  },
  windowStatusExpired: {
    color: COLORS.warningDark,
  },
  inputContainer: {
    display: 'flex',
    alignItems: isMobile ? 'stretch' : 'center',
    gap: '8px',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: isMobile ? '8px' : '6px 8px 6px 16px',
    border: '1px solid rgba(145, 158, 171, 0.24)',
    transition: 'border-color 0.15s',
    '&:focus-within': {
      borderColor: COLORS.primary,
    },
    ...(isMobile && { flexDirection: 'column' }),
  },
  composerPrimaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    minWidth: 0,
  },
  closedWindowComposer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: isMobile ? '8px' : '6px 8px 6px 10px',
    border: '1px solid rgba(145, 158, 171, 0.24)',
    ...(isMobile && { flexDirection: 'column', alignItems: 'stretch' }),
  },
  closedWindowButton: {
    flexShrink: 0,
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 700,
    borderRadius: '8px',
    px: 1.25,
    py: 0.65,
    backgroundColor: COLORS.primary,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: COLORS.primaryDark,
      boxShadow: 'none',
    },
  },
  // Closed-window Personal Reply — slim caption above each chip row + the chip row.
  personalReplyRowLabel: {
    fontSize: '9.5px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: COLORS.textSecondary,
    m: '0 0 3px',
  },
  personalReplyChipRow: {
    display: 'flex',
    gap: '5px',
    mb: '7px',
  },
  closedWindowTemplateSearchWrap: {
    position: 'relative',
    flex: 1,
    minWidth: 220,
    ...(isMobile && { width: '100%', minWidth: 0, flexBasis: 'auto' }),
  },
  closedWindowTemplateSearch: {
    width: '100%',
    height: 36,
    borderRadius: '7px',
    border: 'none',
    outline: 'none',
    padding: '0 10px',
    fontSize: '13px',
    fontFamily: 'inherit',
    color: COLORS.textPrimary,
    backgroundColor: 'rgba(145, 158, 171, 0.08)',
    '&:focus': {
      borderColor: COLORS.primary,
    },
    '&::placeholder': {
      color: COLORS.textDisabled,
    },
  },
  closedWindowTemplateResults: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 'calc(100% + 8px)',
    zIndex: 1000,
    maxHeight: 260,
    overflowY: 'auto',
    borderRadius: '12px',
    border: `1px solid ${COLORS.divider}`,
    backgroundColor: COLORS.paper,
    boxShadow: '0 18px 40px -12px rgba(17, 24, 39, 0.22), 0 2px 8px rgba(17, 24, 39, 0.08)',
  },
  closedWindowTemplateResult: {
    padding: '10px 12px',
    cursor: 'pointer',
    borderBottom: `1px solid ${COLORS.dividerLighter}`,
    '&:hover': {
      backgroundColor: COLORS.primaryLighter,
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  closedWindowTemplateName: {
    fontSize: '13px',
    fontWeight: 700,
    color: COLORS.textPrimary,
    lineHeight: 1.25,
  },
  closedWindowTemplateBody: {
    fontSize: '12px',
    color: COLORS.textSecondary,
    lineHeight: 1.35,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: '3px',
  },
  closedWindowTemplateEmpty: {
    padding: '14px',
    fontSize: '13px',
    color: COLORS.textDisabled,
    textAlign: 'center',
  },
  embeddedToolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    marginBottom: '4px',
    borderBottom: `1px solid ${COLORS.divider}`,
  },
  embeddedInputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: '4px 6px 4px 12px',
    border: '1px solid rgba(145, 158, 171, 0.24)',
    transition: 'border-color 0.15s',
    '&:focus-within': {
      borderColor: COLORS.primary,
    },
  },
  inputBtn: {
    width: isMobile ? '44px' : '36px',
    height: isMobile ? '44px' : '36px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'transparent',
    color: COLORS.textSecondary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.16)',
    },
    ...(isMobile && { minWidth: '44px', minHeight: '44px' }),
  },
  inputBox: {
    flex: 1,
    minWidth: 0,
    padding: '8px 0',
    border: 'none',
    backgroundColor: 'transparent',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'none',
    minHeight: '24px',
    maxHeight: '150px',
    overflowY: 'auto',
    lineHeight: '1.5',
  },
  templateBtn: {
    flexShrink: 0,
    padding: '8px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    border: `1px solid ${COLORS.divider}`,
    backgroundColor: COLORS.paper,
    color: COLORS.textPrimary,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    textTransform: 'none',
    '&:hover': {
      borderColor: COLORS.primary,
      backgroundColor: COLORS.primaryLighter,
    },
  },
  sendBtn: {
    flexShrink: 0,
    width: isMobile ? '44px' : '34px',
    height: isMobile ? '44px' : '34px',
    minWidth: isMobile ? '44px' : '34px',
    borderRadius: '50%',
    backgroundColor: COLORS.primary,
    color: 'white',
    transition: 'transform 140ms cubic-bezier(.2,.7,.3,1.2), background-color 120ms ease',
    '&:hover': {
      backgroundColor: COLORS.primaryDark,
    },
    // Press feedback — a subtle squish on tap/click (used by both the
    // open-window and closed-mode round send buttons).
    '&:active': {
      transform: 'scale(0.94)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'background-color 120ms ease',
      '&:active': { transform: 'none' },
    },
  },
  sendBtnDisabled: {
    backgroundColor: 'rgba(145, 158, 171, 0.24)',
    color: COLORS.textDisabled,
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.24)',
    },
  },
  composerToolsPaper: {
    borderRadius: '12px',
    border: `1px solid ${COLORS.divider}`,
    boxShadow: '0 16px 36px -12px rgba(17, 24, 39, 0.22), 0 2px 8px rgba(17, 24, 39, 0.08)',
    minWidth: '230px',
    overflow: 'hidden',
    backgroundColor: COLORS.paper,
  },
  composerToolsItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minHeight: '38px',
    px: 1.5,
    fontSize: '13px',
    fontWeight: 500,
    color: COLORS.textPrimary,
    '& svg': {
      color: COLORS.textSecondary,
      flexShrink: 0,
    },
  },
  composerFormatSection: {
    px: 1.25,
    py: 0.75,
  },
  composerToolsLabel: {
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: 1,
    color: COLORS.textDisabled,
    textTransform: 'uppercase',
    mb: 0.75,
  },
  // ============================================
  // ATTACHMENT POPOVER (+ button)
  // ============================================
  // Apple-style popover: rounded 14 px card, soft layered shadow, hairline
  // border for crispness at the edges. Opens from the `+` button in the
  // composer action rail.
  attachmentPopoverPaper: {
    borderRadius: '14px',
    border: `1px solid ${COLORS.divider}`,
    boxShadow: '0 20px 44px -12px rgba(17, 24, 39, 0.22), 0 2px 6px rgba(17, 24, 39, 0.06)',
    overflow: 'hidden',
    minWidth: '232px',
    backgroundColor: COLORS.paper,
  },
  attachmentPopoverItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: COLORS.textPrimary,
    transition: 'background-color 100ms ease',
    '&:hover': {
      backgroundColor: COLORS.dividerLighter,
    },
  },
  attachmentPopoverIconWrap: {
    width: '34px',
    height: '34px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#FFFFFF',
  },
  attachmentPopoverLabel: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.2,
    color: COLORS.textPrimary,
  },
  attachmentPopoverSublabel: {
    fontSize: '11.5px',
    color: COLORS.textDisabled,
    lineHeight: 1.2,
    marginTop: '2px',
  },
  attachmentPopoverTextCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  templateInputContainer: {
    display: 'flex',
    alignItems: isMobile ? 'stretch' : 'center',
    gap: '12px',
    width: '100%',
    ...(isMobile && { flexDirection: 'column' }),
  },
  templateSelectBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '8px',
    border: `1px solid rgba(145, 158, 171, 0.32)`,
    backgroundColor: COLORS.paper,
    color: COLORS.textPrimary,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    flexShrink: 0,
    textTransform: 'none',
    '&:hover': {
      borderColor: COLORS.primary,
      backgroundColor: COLORS.primaryLighter,
    },
  },
  templateSearchInput: {
    flex: 1,
    fontSize: '13px',
    color: COLORS.textPrimary,
    padding: '10px 16px',
    backgroundColor: COLORS.background,
    borderRadius: '10px',
    border: `1px solid ${COLORS.divider}`,
    outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
    '&:focus': {
      borderColor: COLORS.primary,
    },
    '&::placeholder': {
      color: COLORS.textDisabled,
    },
  },
  templateDropdown: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    right: 0,
    marginBottom: '6px',
    backgroundColor: COLORS.paper,
    borderRadius: '12px',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.12)',
    border: `1px solid ${COLORS.divider}`,
    zIndex: isMobile ? 1200 : 1000,
    maxHeight: isMobile ? 'calc(100dvh - 200px)' : '320px',
    overflowY: 'auto',
  },
  templateDropdownItem: {
    padding: '10px 14px',
    cursor: 'pointer',
    transition: 'background-color 0.1s',
    borderBottom: `1px solid ${COLORS.dividerLighter}`,
    '&:hover': {
      backgroundColor: COLORS.primaryLighter,
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  templatePreviewBubble: {
    maxWidth: '65%',
    padding: '12px 14px',
    backgroundColor: COLORS.sent,
    borderRadius: '8px',
    border: `1.5px dashed ${COLORS.primary}`,
    position: 'relative',
  },
  attachmentPreviewBubble: {
    maxWidth: isMobile ? '90%' : '70%',
    padding: '12px 14px',
    backgroundColor: COLORS.sent,
    borderRadius: '8px',
    border: `1.5px dashed ${COLORS.primary}`,
    position: 'relative',
  },
  attachmentThumbnail: {
    maxWidth: '100%',
    maxHeight: '200px',
    borderRadius: '6px',
    objectFit: 'contain',
  },
  attachmentCaptionInput: {
    width: '100%',
    padding: '8px 10px',
    fontSize: '13px',
    border: `1px solid ${COLORS.divider}`,
    borderRadius: '6px',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'none',
    marginTop: '8px',
    '&:focus': {
      borderColor: COLORS.primary,
    },
  },
  templateInputHint: {
    flex: 1,
    fontSize: '13px',
    color: COLORS.textDisabled,
    padding: '10px 16px',
    backgroundColor: COLORS.background,
    borderRadius: '10px',
  },
  templateSendBtn: {
    width: '44px',
    height: '44px',
    minWidth: '44px',
    borderRadius: '12px',
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(0, 167, 111, 0.24)',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 16px rgba(0, 167, 111, 0.32)',
    },
  },

  // ============================================
  // SCROLL TO BOTTOM BUTTON
  // Native-WA / Apple-style circular FAB. Sits bottom-right of the chat
  // pane (anchored to containerStyle's relative root), above the composer
  // footer. White surface + grey arrow + soft shadow — unobtrusive until
  // needed. Unread-count badge overlays top-right in brand green.
  // ============================================
  scrollToBottomBtn: {
    position: 'absolute',
    // Sits above the composer footer (~80 px tall on desktop,
    // ~96 px on mobile). Right-inset matches the 16 px pane gutter.
    bottom: isMobile ? '108px' : '92px',
    right: isMobile ? '12px' : '20px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: COLORS.paper,
    color: COLORS.textSecondary,
    border: `1px solid ${COLORS.divider}`,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.06)',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, opacity 0.2s ease',
    opacity: 0.98,
    '&:hover': {
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.16), 0 1px 3px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-1px)',
      color: COLORS.textPrimary,
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  scrollToBottomBtnBadge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    minWidth: '18px',
    height: '18px',
    padding: '0 5px',
    borderRadius: '9px',
    backgroundColor: COLORS.primary,
    color: '#fff',
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: '18px',
    textAlign: 'center',
    boxShadow: '0 0 0 2px #fff',
    pointerEvents: 'none',
  },

  // ============================================
  // AI ASSISTANT STYLES
  // ============================================
  aiAssistantBtn: {
    minWidth: '36px',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    backgroundColor: 'rgba(0, 167, 111, 0.08)',
    color: COLORS.primary,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(0, 167, 111, 0.16)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  aiAssistantBtnActive: {
    backgroundColor: COLORS.primary,
    color: '#fff',
    '&:hover': {
      backgroundColor: COLORS.primaryDark,
    },
  },

  // AI Menu Popover
  aiMenu: {
    minWidth: 280,
    maxWidth: 320,
    padding: '8px',
    borderRadius: '12px',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
  },
  aiMenuHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px 12px',
    borderBottom: `1px solid ${COLORS.divider}`,
    marginBottom: '8px',
  },
  aiMenuTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  aiMenuCategory: {
    padding: '6px 12px 4px',
    fontSize: '11px',
    fontWeight: 600,
    color: COLORS.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  aiMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.08)',
    },
  },
  aiMenuItemIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  aiMenuItemText: {
    flex: 1,
  },
  aiMenuItemLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: COLORS.textPrimary,
  },
  aiMenuItemDesc: {
    fontSize: '11px',
    color: COLORS.textSecondary,
    marginTop: '2px',
  },

  // AI Result Panel
  aiResultPanel: {
    margin: '0 0 12px',
    padding: '14px 16px',
    borderRadius: '12px',
    backgroundColor: 'rgba(0, 167, 111, 0.06)',
    border: '1px solid rgba(0, 167, 111, 0.20)',
  },
  aiResultPanelError: {
    backgroundColor: 'rgba(255, 86, 48, 0.06)',
    border: '1px solid rgba(255, 86, 48, 0.20)',
  },
  aiResultHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  aiResultTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 600,
    color: COLORS.primary,
  },
  aiResultTitleError: {
    color: COLORS.errorDark,
  },
  aiResultCloseBtn: {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    color: COLORS.textSecondary,
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.16)',
    },
  },
  aiResultContent: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: COLORS.textPrimary,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '200px',
    overflowY: 'auto',
  },
  aiResultActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    flexWrap: 'wrap',
  },
  aiResultActionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s',
    backgroundColor: COLORS.paper,
    color: COLORS.textSecondary,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.16)',
    },
  },
  aiResultActionBtnPrimary: {
    backgroundColor: COLORS.primary,
    color: '#fff',
    '&:hover': {
      backgroundColor: COLORS.primaryDark,
    },
  },

  // AI Loading State
  aiLoadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 0',
  },
  aiLoadingText: {
    fontSize: '13px',
    color: COLORS.textSecondary,
  },

  // Language Picker (for Translate)
  aiLanguagePicker: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '8px 12px',
    borderTop: `1px solid ${COLORS.divider}`,
    marginTop: '4px',
  },
  aiLanguageBtn: {
    padding: '6px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 500,
    border: `1px solid ${COLORS.divider}`,
    backgroundColor: COLORS.paper,
    color: COLORS.textSecondary,
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: COLORS.primary,
      color: COLORS.primary,
    },
  },
  aiLanguageBtnActive: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
    color: '#fff',
    '&:hover': {
      backgroundColor: COLORS.primaryDark,
      borderColor: COLORS.primaryDark,
    },
  },

  // ============================================
  // MOBILE STYLES
  // ============================================
  footerMobile: {
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
  mobileBottomSheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1300,
    borderRadius: '16px 16px 0 0',
    boxShadow: '0 -8px 24px rgba(0,0,0,0.15)',
    backgroundColor: COLORS.paper,
    overflow: 'hidden',
  },
  mobileBottomSheetBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1299,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  mobileFullScreenPanel: {
    position: 'fixed',
    inset: 0,
    width: '100%',
    // dvh + safe-area insets keep the panel clear of the notch (top) and the
    // home indicator (bottom) on iOS. env() resolves to 0 on devices without
    // safe areas, so this is a no-op there.
    height: '100dvh',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    zIndex: 1200,
    backgroundColor: COLORS.paper,
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 250ms ease',
  },
  mobileFullScreenPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.divider}`,
  },
});

// ============================================
// CONTACTS PANEL STYLES
// ============================================
// eslint-disable-next-line no-unused-vars
export const getContactsStyles = (theme, { width, isMobile }) => ({
  wrapper: {
    width: isMobile ? '100%' : '380px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: COLORS.paper,
    borderRight: `1px solid ${COLORS.divider}`,
    overflow: 'hidden',
    position: 'relative',
  },
  header: {
    padding: '14px 16px 12px',
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  practiceAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    overflow: 'hidden',
    cursor: 'pointer',
  },

  // Quick Filters
  queueHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '2px',
  },
  viewsIconButton: {
    flexShrink: 0,
    width: 30,
    height: 30,
    borderRadius: '8px',
    border: 'none',
    color: COLORS.textSecondary,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: COLORS.dividerLighter,
      color: COLORS.textPrimary,
      boxShadow: 'none',
    },
  },
  viewsIconButtonActive: {
    color: COLORS.primary,
    backgroundColor: COLORS.primaryLighter,
  },
  viewsMenuPaper: {
    borderRadius: '10px',
    minWidth: 190,
    mt: 0.5,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    border: `1px solid ${COLORS.divider}`,
  },
  viewsMenuItem: {
    gap: 1.25,
    fontSize: '13px',
    py: 1,
    minHeight: 36,
  },
  viewsMenuCount: {
    marginLeft: 'auto',
    minWidth: 20,
    height: 18,
    borderRadius: '9px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 0.75,
    backgroundColor: COLORS.dividerLighter,
    color: COLORS.textSecondary,
    fontSize: '10px',
    fontWeight: 700,
  },
  quickFilters: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '4px',
    marginBottom: '10px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${COLORS.dividerLight}`,
  },
  filterBtn: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '6px 5px',
    borderRadius: '999px',
    border: 'none',
    backgroundColor: 'transparent',
    color: COLORS.textSecondary,
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    minHeight: '32px',
    textTransform: 'none',
    lineHeight: 1,
    '&:hover': {
      color: COLORS.primary,
      backgroundColor: COLORS.dividerLighter,
    },
    ...(isMobile && { minHeight: '36px' }),
  },
  filterBtnActive: {
    color: COLORS.primary,
    fontWeight: 700,
    backgroundColor: COLORS.primaryLighter,
    '&:hover': {
      backgroundColor: COLORS.primaryLighter,
    },
  },
  filterBtnLabel: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '11px',
    fontWeight: 700,
  },
  followUpCount: {
    backgroundColor: COLORS.primary,
    color: '#fff',
    fontSize: '9px',
    fontWeight: 700,
    padding: '1px 5px',
    borderRadius: '9px',
    lineHeight: '15px',
    minWidth: '17px',
    textAlign: 'center',
    flexShrink: 0,
  },
  pendingCount: {
    backgroundColor: COLORS.warning,
    color: '#fff',
    fontSize: '9px',
    fontWeight: 700,
    padding: '1px 5px',
    borderRadius: '9px',
    lineHeight: '15px',
    minWidth: '17px',
    textAlign: 'center',
    flexShrink: 0,
  },
  treatmentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '10px',
    fontWeight: 500,
    color: '#637381',
    backgroundColor: 'rgba(145, 158, 171, 0.12)',
    borderRadius: '4px',
    padding: '1px 6px',
    lineHeight: '16px',
    maxWidth: '140px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: '2px',
  },
  treatmentChipsContainer: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    marginTop: '2px',
  },

  // Search
  searchContainer: {
    position: 'relative',
    marginBottom: '12px',
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: COLORS.textDisabled,
    width: '16px',
    height: '16px',
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px 8px 38px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '13px',
    outline: 'none',
    backgroundColor: COLORS.dividerLighter,
    color: COLORS.textPrimary,
    transition: 'all 0.15s',
    '&::placeholder': {
      color: COLORS.textDisabled,
    },
    '&:focus': {
      backgroundColor: 'rgba(145, 158, 171, 0.16)',
    },
  },

  // Status Tabs (Chips)
  statusTabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '8px 0',
  },
  statusTab: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    borderRadius: '16px',
    border: 'none',
    backgroundColor: COLORS.dividerLight,
    color: COLORS.textSecondary,
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    height: '28px',
    textTransform: 'none',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.24)',
    },
  },
  statusTabActive: {
    backgroundColor: COLORS.primaryLight,
    color: COLORS.primaryDark,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: COLORS.primaryLight,
    },
  },
  statusTabCount: {
    backgroundColor: 'transparent',
    color: COLORS.textSecondary,
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '10px',
    marginLeft: '6px',
  },
  statusTabCountActive: {
    backgroundColor: 'transparent',
    color: COLORS.primaryDark,
  },

  // Contact List
  contactList: {
    flex: 1,
    overflowY: 'auto',
  },
  bulkArchiveNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    mt: 1.5,
    p: 1,
    borderRadius: '8px',
    backgroundColor: COLORS.dividerLighter,
    color: COLORS.textSecondary,
  },
  contactSectionDivider: {
    padding: '8px 16px',
    backgroundColor: COLORS.background,
    borderBottom: `1px solid ${COLORS.dividerLight}`,
  },
  contactSectionDividerText: {
    fontSize: '11px',
    fontWeight: 600,
    color: COLORS.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  contactItem: {
    padding: '10px 16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    cursor: 'pointer',
    borderLeft: '3px solid transparent',
    transition: 'all 0.15s',
    position: 'relative',
    '&:hover': {
      backgroundColor: COLORS.dividerLighter,
    },
  },
  contactItemActive: {
    backgroundColor: 'rgba(0, 167, 111, 0.14)',
    borderLeftColor: COLORS.primary,
    '&:hover': {
      backgroundColor: 'rgba(0, 167, 111, 0.18)',
    },
  },
  contactItemUnread: {
    '& .contact-name': {
      fontWeight: 700,
    },
    '& .contact-message': {
      color: COLORS.textPrimary,
      fontWeight: 500,
    },
  },
  contactAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
    position: 'relative',
    flexShrink: 0,
  },
  onlineIndicator: {
    position: 'absolute',
    bottom: '2px',
    right: '2px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#22C55E',
    border: '2px solid white',
  },
  unreadBadge: {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    minWidth: '18px',
    height: '18px',
    borderRadius: '9px',
    backgroundColor: COLORS.primary,
    color: 'white',
    fontSize: '10px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
    border: '2px solid white',
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
  },
  contactNameRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2px',
    gap: '8px',
  },
  contactName: {
    fontWeight: 600,
    fontSize: '14px',
    color: COLORS.textPrimary,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  newBadge: {
    backgroundColor: COLORS.warning,
    color: COLORS.warningLight,
    fontSize: '10px',
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: '6px',
    flexShrink: 0,
  },
  contactTime: {
    fontSize: '11px',
    color: COLORS.textDisabled,
    flexShrink: 0,
  },
  contactTimeUnread: {
    color: COLORS.primary,
    fontWeight: 600,
  },
  contactMessageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  messageStatus: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    color: COLORS.textDisabled,
  },
  messageStatusRead: {
    color: COLORS.primary,
  },
  contactMessage: {
    fontSize: '13px',
    color: COLORS.textSecondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
  },
  typingIndicator: {
    color: COLORS.primary,
    fontStyle: 'italic',
  },

  // Hover Actions
  hoverActions: {
    position: 'absolute',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    gap: '4px',
    opacity: 0,
    transition: 'opacity 0.15s',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9) 20%)',
    paddingLeft: '20px',
  },
  hoverActionBtn: {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: 'white',
    color: COLORS.textSecondary,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 0.15s',
    '&:hover': {
      backgroundColor: COLORS.background,
      color: COLORS.textPrimary,
    },
  },
  contactItemClosing: {
    opacity: 0,
    transform: 'translateX(-60px)',
    maxHeight: 0,
    padding: '0 1rem',
    overflow: 'hidden',
    transition: 'opacity 0.3s ease, transform 0.3s ease, max-height 0.3s ease 0.1s, padding 0.3s ease 0.1s',
    pointerEvents: 'none',
  },

  // ============================================
  // BULK SELECT — sidebar bulk-action bar + select-mode row affordances
  // ============================================
  // Leading-edge checkbox column. Default state: invisible. Reveals
  // on row hover (opacity 0.6) or when select mode is active (1.0).
  // Sized so it doesn't reflow the avatar+name row when toggling.
  rowSelectCheckbox: {
    width: '24px',
    height: '24px',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease',
    // Hidden by default — parent's :hover rule lifts it.
    opacity: 0,
    color: COLORS.textSecondary,
    // Hover/focus on the checkbox itself shouldn't trigger the
    // row's hover background flicker — pointer-events stays on
    // since users need to click it.
    '&:focus-visible': {
      opacity: 1,
      outline: `2px solid ${COLORS.primary}`,
      outlineOffset: '2px',
    },
  },
  rowSelectCheckboxVisible: {
    // Always-visible variant used when select mode is on.
    opacity: 1,
    color: COLORS.textSecondary,
  },
  rowSelectCheckboxChecked: {
    // Selected state — filled green circle with white check.
    opacity: 1,
    backgroundColor: COLORS.primary,
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: COLORS.primaryDark,
    },
  },
  // Hover-only reveal for the unchecked checkbox. Selector matches
  // any descendant inside a hovered ContactItemWrapper (the styled
  // component already exposes `&:hover .hover-actions` so we can
  // chain a sibling rule for the checkbox).
  contactItemHoverReveal: {
    // Applied directly to the row wrapper sx — adds the rule
    // to lift the row-checkbox opacity on hover. Used in
    // Contacts.jsx via a passthrough in the row's sx.
    '&:hover .row-select-checkbox': {
      opacity: 0.6,
    },
    '&:hover .row-select-checkbox:hover': {
      opacity: 1,
    },
  },
  // Selected-row tint. Layered ON TOP of any active-conversation
  // styling so a row that is BOTH currently-open AND selected
  // shows the blue selection wash, not the green active stripe.
  contactItemSelected: {
    backgroundColor: 'rgba(33, 150, 243, 0.10)',
    borderLeftColor: '#1976D2',
    '&:hover': {
      backgroundColor: 'rgba(33, 150, 243, 0.16)',
    },
  },
  // "Select" toolbar button — sits next to the search input when
  // not in select mode. Mirrors the More-menu icon button styling.
  selectModeToggleBtn: {
    width: 32,
    height: 32,
    borderRadius: '8px',
    color: COLORS.textSecondary,
    backgroundColor: COLORS.dividerLighter,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(145, 158, 171, 0.16)',
      color: COLORS.textPrimary,
    },
  },
});

// ============================================
// DETAILS PANEL STYLES
// ============================================
// eslint-disable-next-line no-unused-vars
export const getDetailsPanelStyles = (theme) => ({
  wrapper: {
    width: '320px',
    backgroundColor: COLORS.paper,
    borderLeft: `1px solid ${COLORS.divider}`,
    display: 'none',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  wrapperOpen: {
    display: 'flex',
  },
  header: {
    padding: '16px 20px',
    borderBottom: `1px solid ${COLORS.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
  },
  avatarSection: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: '28px',
    margin: '0 auto 12px',
  },
  name: {
    fontSize: '18px',
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: '4px',
  },
  phone: {
    fontSize: '13px',
    color: COLORS.textSecondary,
  },

  // Quick Stats
  quickStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '12px',
    backgroundColor: COLORS.background,
    borderRadius: '10px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '12px',
    color: COLORS.textSecondary,
  },

  // Info Section
  infoSection: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: COLORS.textDisabled,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  infoLabel: {
    fontSize: '13px',
    color: COLORS.textSecondary,
  },
  infoValue: {
    fontSize: '13px',
    fontWeight: 500,
    color: COLORS.textPrimary,
  },
  infoValueMuted: {
    color: COLORS.textDisabled,
  },
  infoValueBadge: {
    fontSize: '12px',
    fontWeight: 600,
    color: COLORS.primaryDark,
    backgroundColor: COLORS.primaryLight,
    padding: '4px 8px',
    borderRadius: '6px',
  },

  // Tags
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  tag: {
    padding: '6px 10px',
    backgroundColor: 'rgba(255, 171, 0, 0.16)',
    color: COLORS.warningDark,
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '6px',
  },
  addTagBtn: {
    padding: '6px 10px',
    backgroundColor: 'transparent',
    border: `1px dashed rgba(145, 158, 171, 0.32)`,
    color: COLORS.textSecondary,
    fontSize: '12px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: COLORS.primary,
      color: COLORS.primary,
    },
  },

  // Action Links
  actionLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderRadius: '10px',
    border: `1px solid ${COLORS.divider}`,
    backgroundColor: 'white',
    cursor: 'pointer',
    width: '100%',
    marginBottom: '8px',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: COLORS.primary,
    },
  },
  actionLinkContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13px',
    color: COLORS.textPrimary,
  },
});

// ============================================
// MODAL STYLES
// ============================================
// eslint-disable-next-line no-unused-vars
export const getModalStyles = (theme) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '24px',
    width: '400px',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
  },
  modalWide: {
    width: '520px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  icon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: COLORS.primaryLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.primary,
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  subtitle: {
    fontSize: '14px',
    color: COLORS.textSecondary,
    marginTop: '4px',
  },
  body: {
    backgroundColor: COLORS.background,
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '20px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
  },
  btnSecondary: {
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    border: `1px solid rgba(145, 158, 171, 0.32)`,
    backgroundColor: 'transparent',
    color: COLORS.textPrimary,
    '&:hover': {
      backgroundColor: COLORS.dividerLighter,
    },
  },
  btnPrimary: {
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    border: 'none',
    backgroundColor: COLORS.primary,
    color: 'white',
    '&:hover': {
      backgroundColor: COLORS.primaryDark,
    },
  },
});

// ============================================
// TEMPLATE OPTION STYLES
// ============================================
// eslint-disable-next-line no-unused-vars
export const getTemplateStyles = (theme) => ({
  option: {
    padding: '14px 16px',
    border: `1px solid ${COLORS.divider}`,
    borderRadius: '10px',
    marginBottom: '10px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': {
      borderColor: 'rgba(0, 167, 111, 0.5)',
      backgroundColor: 'rgba(0, 167, 111, 0.04)',
    },
  },
  optionSelected: {
    borderColor: COLORS.primary,
    backgroundColor: COLORS.primaryLighter,
  },
  optionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  optionName: {
    fontSize: '14px',
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  optionCategory: {
    fontSize: '11px',
    fontWeight: 500,
    color: COLORS.textSecondary,
    backgroundColor: 'rgba(145, 158, 171, 0.16)',
    padding: '3px 8px',
    borderRadius: '6px',
  },
  optionPreview: {
    fontSize: '13px',
    color: COLORS.textSecondary,
    lineHeight: 1.5,
    margin: 0,
  },
  optionCode: {
    backgroundColor: COLORS.primaryLighter,
    color: COLORS.primaryDark,
    padding: '1px 4px',
    borderRadius: '4px',
    fontFamily: 'inherit',
    fontSize: '12px',
  },
});

// Color utilities for avatars
export const AVATAR_COLORS = {
  green: '#22C55E',
  indigo: '#6366F1',
  amber: '#F59E0B',
  red: '#EF4444',
  cyan: '#00B8D9',
  purple: '#8E33FF',
  pink: '#FF5C93',
  blue: '#2065D1',
};

export const getAvatarColor = (name) => {
  const colors = Object.values(AVATAR_COLORS);
  const index = (name?.charCodeAt(0) || 0) % colors.length;
  return colors[index];
};

export default getStyles;
